import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router";
import { useGet, usePut } from "../../api";
import {
  IKindredOfferDetail,
  IKindredOfferUpdate,
  IKindredEntityUpdateRequest,
  IKindredOfferMetadata,
  IKindredCouponMetadata,
  IKindredMetadataUpdate,
  KindredPublishStatus,
  KindredPromoType,
  KindredPromoCoverage,
  KindredPromoScope,
  KindredPromoChannel,
  KindredDiscountType,
  KINDRED_NON_MONETARY_VALUES,
  OfferOverridesFlag,
} from "../../interfaces";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { Copy, Check, Edit2, X, Save, Plus, Minus } from "react-feather";
import { format } from "date-fns";
import {
  BrandTypeahead,
  TagListEditor,
  OverrideFlagsEditor,
  OverrideFlagWarning,
  missingOverrides,
  withOverrides,
  OFFER_OVERRIDE_REQUIREMENTS,
} from "../../components/workbench";

const statusLabel = (status: number) => {
  const map: Record<number, { bg: string; label: string }> = {
    [KindredPublishStatus.Draft]: { bg: "secondary", label: "Draft" },
    [KindredPublishStatus.AwaitingApproval]: {
      bg: "warning",
      label: "Awaiting Approval",
    },
    [KindredPublishStatus.Approved]: { bg: "success", label: "Approved" },
    [KindredPublishStatus.Deactivated]: {
      bg: "danger",
      label: "Deactivated",
    },
  };
  const info = map[status] || { bg: "secondary", label: String(status) };
  return <Badge bg={info.bg}>{info.label}</Badge>;
};

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return "—";
  try {
    return format(new Date(dateStr), "yyyy-MM-dd HH:mm");
  } catch {
    return dateStr;
  }
};

const promoTypeLabel = (val: number | null) => {
  if (val == null) return "—";
  const map: Record<number, string> = {
    [KindredPromoType.None]: "None",
    [KindredPromoType.Monetary]: "Monetary",
    [KindredPromoType.NonMonetary]: "Non-Monetary",
  };
  return map[val] ?? String(val);
};

const promoCoverageLabel = (val: number | null) => {
  if (val == null) return "—";
  const map: Record<number, string> = {
    [KindredPromoCoverage.None]: "None",
    [KindredPromoCoverage.SiteWide]: "Site-Wide",
    [KindredPromoCoverage.Category]: "Category",
    [KindredPromoCoverage.Product]: "Product",
  };
  return map[val] ?? String(val);
};

const promoScopeLabel = (val: number | null) => {
  if (val == null) return "—";
  const map: Record<number, string> = {
    [KindredPromoScope.None]: "None",
    [KindredPromoScope.New]: "New Customers",
    [KindredPromoScope.Existing]: "Existing Customers",
    [KindredPromoScope.All]: "All Customers",
  };
  return map[val] ?? String(val);
};

const promoChannelLabel = (val: number | null) => {
  if (val == null) return "—";
  const map: Record<number, string> = {
    [KindredPromoChannel.None]: "None",
    [KindredPromoChannel.Online]: "Online",
    [KindredPromoChannel.InStore]: "In-Store",
  };
  return map[val] ?? String(val);
};

const discountTypeLabel = (val: number | null) => {
  if (val == null) return "—";
  const map: Record<number, string> = {
    [KindredDiscountType.None]: "None",
    [KindredDiscountType.Percentage]: "Percentage",
    [KindredDiscountType.Fixed]: "Fixed",
    [KindredDiscountType.Others]: "Others",
  };
  return map[val] ?? String(val);
};

const boolBadge = (val: boolean | null) => {
  if (val == null) return <span className="text-muted">—</span>;
  return val ? (
    <Badge bg="success">Yes</Badge>
  ) : (
    <Badge bg="secondary">No</Badge>
  );
};

interface DiscountEntry {
  discountValue?: number | null;
  maxDiscountValue?: number | null;
  minimumSpend?: number | null;
}

const DiscountsDisplay: React.FC<{
  raw: string | null;
  currency: string | null;
}> = ({ raw, currency }) => {
  if (!raw) return <span className="text-muted">—</span>;
  try {
    const entries: DiscountEntry[] = JSON.parse(raw);
    if (!Array.isArray(entries) || entries.length === 0)
      return <span className="text-muted">—</span>;
    const cur = currency ? ` ${currency}` : "";
    return (
      <div className="d-flex flex-column gap-1">
        {entries.map((e, i) => {
          const parts: string[] = [];
          if (e.discountValue != null) parts.push(`${e.discountValue}${cur}`);
          if (e.maxDiscountValue != null)
            parts.push(`max ${e.maxDiscountValue}${cur}`);
          if (e.minimumSpend != null)
            parts.push(`min spend ${e.minimumSpend}${cur}`);
          return (
            <span key={i}>
              {parts.length > 0 ? parts.join(" · ") : "No values"}
            </span>
          );
        })}
      </div>
    );
  } catch {
    return <code className="small">{raw}</code>;
  }
};

const DiscountsEditor: React.FC<{
  value: string | null;
  onChange: (val: string | null) => void;
}> = ({ value, onChange }) => {
  const parseEntries = (raw: string | null): DiscountEntry[] => {
    if (!raw) return [];
    try {
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  };

  const entries = parseEntries(value);

  const update = (newEntries: DiscountEntry[]) => {
    onChange(newEntries.length > 0 ? JSON.stringify(newEntries) : null);
  };

  const addEntry = () => {
    update([
      ...entries,
      { discountValue: null, maxDiscountValue: null, minimumSpend: null },
    ]);
  };

  const removeEntry = (idx: number) => {
    update(entries.filter((_, i) => i !== idx));
  };

  const updateEntry = (
    idx: number,
    field: keyof DiscountEntry,
    val: string
  ) => {
    const updated = [...entries];
    updated[idx] = {
      ...updated[idx],
      [field]: val === "" ? null : Number(val),
    };
    update(updated);
  };

  return (
    <div>
      {entries.map((entry, i) => (
        <div key={i} className="d-flex gap-2 align-items-center mb-2">
          <Form.Control
            size="sm"
            type="number"
            placeholder="Discount"
            style={{ width: 100 }}
            value={entry.discountValue ?? ""}
            onChange={(e) => updateEntry(i, "discountValue", e.target.value)}
          />
          <Form.Control
            size="sm"
            type="number"
            placeholder="Max discount"
            style={{ width: 120 }}
            value={entry.maxDiscountValue ?? ""}
            onChange={(e) => updateEntry(i, "maxDiscountValue", e.target.value)}
          />
          <Form.Control
            size="sm"
            type="number"
            placeholder="Min spend"
            style={{ width: 110 }}
            value={entry.minimumSpend ?? ""}
            onChange={(e) => updateEntry(i, "minimumSpend", e.target.value)}
          />
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => removeEntry(i)}
            title="Remove"
          >
            <Minus size={12} />
          </Button>
        </div>
      ))}
      <Button variant="outline-primary" size="sm" onClick={addEntry}>
        <Plus size={12} className="me-1" />
        Add Discount
      </Button>
    </div>
  );
};

const MetadataRow: React.FC<{
  label: string;
  children: React.ReactNode;
  hide?: boolean;
}> = ({ label, children, hide }) => {
  if (hide) return null;
  return (
    <tr>
      <td className="fw-bold" style={{ width: "30%" }}>
        {label}
      </td>
      <td>{children}</td>
    </tr>
  );
};

export const MetadataCard: React.FC<{
  metadata: IKindredCouponMetadata | IKindredOfferMetadata | null;
  hashLabel: string;
  hashValue?: string;
  editing?: boolean;
  metadataForm?: IKindredMetadataUpdate;
  onMetadataChange?: (field: keyof IKindredMetadataUpdate, value: any) => void;
}> = ({
  metadata,
  hashLabel,
  hashValue,
  editing,
  metadataForm,
  onMetadataChange,
}) => {
  const updateMeta = (field: keyof IKindredMetadataUpdate, value: any) => {
    onMetadataChange?.(field, value);
  };

  const parseNullableNumber = (val: string): number | null =>
    val === "" ? null : Number(val);

  const parseNullableBool = (val: string): boolean | null =>
    val === "" ? null : val === "true";

  if (!metadata && !editing) {
    return (
      <Card className="mb-3">
        <Card.Header>Metadata</Card.Header>
        <Card.Body className="p-0">
          <Table size="sm" borderless className="mb-0">
            <tbody>
              <MetadataRow label="Promo Type">
                <span className="text-muted">—</span>
              </MetadataRow>
              <MetadataRow label="Coverage">
                <span className="text-muted">—</span>
              </MetadataRow>
              <MetadataRow label="Scope">
                <span className="text-muted">—</span>
              </MetadataRow>
              <MetadataRow label="Channel">
                <span className="text-muted">—</span>
              </MetadataRow>
              <MetadataRow label="Discount Type">
                <span className="text-muted">—</span>
              </MetadataRow>
              <MetadataRow label="Discounts">
                <span className="text-muted">—</span>
              </MetadataRow>
              <MetadataRow label="Non-Monetary Value">
                <span className="text-muted">—</span>
              </MetadataRow>
              <MetadataRow label="Language">
                <span className="text-muted">—</span>
              </MetadataRow>
              <MetadataRow label="Currency">
                <span className="text-muted">—</span>
              </MetadataRow>
              <MetadataRow label="Product Category">
                <span className="text-muted">—</span>
              </MetadataRow>
              <MetadataRow label="Raw Category">
                <span className="text-muted">—</span>
              </MetadataRow>
              <MetadataRow label="Stackable">
                <span className="text-muted">—</span>
              </MetadataRow>
              <MetadataRow label="Requires Review">
                <span className="text-muted">—</span>
              </MetadataRow>
              <MetadataRow label="Reviewed">
                <span className="text-muted">—</span>
              </MetadataRow>
              <MetadataRow label="Reasoning">
                <span className="text-muted">—</span>
              </MetadataRow>
              <MetadataRow label="Created">
                <span className="text-muted">—</span>
              </MetadataRow>
              <MetadataRow label="Updated">
                <span className="text-muted">—</span>
              </MetadataRow>
              <MetadataRow label={hashLabel}>
                <code className="small">{hashValue ?? "—"}</code>
              </MetadataRow>
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
  }

  if (editing && metadataForm) {
    return (
      <Card className="mb-3">
        <Card.Header>Metadata {!metadata && "(New)"}</Card.Header>
        <Card.Body className="p-0">
          <Table size="sm" borderless className="mb-0">
            <tbody>
              <MetadataRow label="Promo Type">
                <Form.Select
                  size="sm"
                  value={metadataForm.promoType ?? ""}
                  onChange={(e) =>
                    updateMeta("promoType", parseNullableNumber(e.target.value))
                  }
                >
                  <option value="">—</option>
                  <option value={KindredPromoType.None}>None</option>
                  <option value={KindredPromoType.Monetary}>Monetary</option>
                  <option value={KindredPromoType.NonMonetary}>
                    Non-Monetary
                  </option>
                </Form.Select>
              </MetadataRow>
              <MetadataRow label="Coverage">
                <Form.Select
                  size="sm"
                  value={metadataForm.coverage ?? ""}
                  onChange={(e) =>
                    updateMeta("coverage", parseNullableNumber(e.target.value))
                  }
                >
                  <option value="">—</option>
                  <option value={KindredPromoCoverage.None}>None</option>
                  <option value={KindredPromoCoverage.SiteWide}>
                    Site-Wide
                  </option>
                  <option value={KindredPromoCoverage.Category}>
                    Category
                  </option>
                  <option value={KindredPromoCoverage.Product}>Product</option>
                </Form.Select>
              </MetadataRow>
              <MetadataRow label="Scope">
                <Form.Select
                  size="sm"
                  value={metadataForm.scope ?? ""}
                  onChange={(e) =>
                    updateMeta("scope", parseNullableNumber(e.target.value))
                  }
                >
                  <option value="">—</option>
                  <option value={KindredPromoScope.None}>None</option>
                  <option value={KindredPromoScope.New}>New Customers</option>
                  <option value={KindredPromoScope.Existing}>
                    Existing Customers
                  </option>
                  <option value={KindredPromoScope.All}>All Customers</option>
                </Form.Select>
              </MetadataRow>
              <MetadataRow label="Channel">
                <Form.Select
                  size="sm"
                  value={metadataForm.channel ?? ""}
                  onChange={(e) =>
                    updateMeta("channel", parseNullableNumber(e.target.value))
                  }
                >
                  <option value="">—</option>
                  <option value={KindredPromoChannel.None}>None</option>
                  <option value={KindredPromoChannel.Online}>Online</option>
                  <option value={KindredPromoChannel.InStore}>In-Store</option>
                </Form.Select>
              </MetadataRow>
              <MetadataRow label="Discount Type">
                <Form.Select
                  size="sm"
                  value={metadataForm.discountType ?? ""}
                  onChange={(e) =>
                    updateMeta(
                      "discountType",
                      parseNullableNumber(e.target.value)
                    )
                  }
                >
                  <option value="">—</option>
                  <option value={KindredDiscountType.None}>None</option>
                  <option value={KindredDiscountType.Percentage}>
                    Percentage
                  </option>
                  <option value={KindredDiscountType.Fixed}>Fixed</option>
                  <option value={KindredDiscountType.Others}>Others</option>
                </Form.Select>
              </MetadataRow>
              <MetadataRow label="Discounts">
                <DiscountsEditor
                  value={metadataForm.discounts ?? null}
                  onChange={(val) => updateMeta("discounts", val)}
                />
              </MetadataRow>
              <MetadataRow label="Non-Monetary Value">
                <Form.Select
                  size="sm"
                  value={metadataForm.nonMonetaryValue ?? ""}
                  onChange={(e) =>
                    updateMeta("nonMonetaryValue", e.target.value || null)
                  }
                >
                  <option value="">—</option>
                  {KINDRED_NON_MONETARY_VALUES.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </Form.Select>
              </MetadataRow>
              <MetadataRow label="Language">
                <Form.Control
                  size="sm"
                  value={metadataForm.language ?? ""}
                  onChange={(e) =>
                    updateMeta("language", e.target.value || null)
                  }
                />
              </MetadataRow>
              <MetadataRow label="Currency">
                <Form.Control
                  size="sm"
                  value={metadataForm.currency ?? ""}
                  onChange={(e) =>
                    updateMeta("currency", e.target.value || null)
                  }
                />
              </MetadataRow>
              <MetadataRow label="Product Category">
                <Form.Control
                  size="sm"
                  value={metadataForm.productCategory ?? ""}
                  onChange={(e) =>
                    updateMeta("productCategory", e.target.value || null)
                  }
                />
              </MetadataRow>
              <MetadataRow label="Raw Category">
                <Form.Control
                  size="sm"
                  value={metadataForm.rawCategory ?? ""}
                  onChange={(e) =>
                    updateMeta("rawCategory", e.target.value || null)
                  }
                />
              </MetadataRow>
              <MetadataRow label="Stackable">
                <Form.Select
                  size="sm"
                  value={
                    metadataForm.isStackable == null
                      ? ""
                      : String(metadataForm.isStackable)
                  }
                  onChange={(e) =>
                    updateMeta("isStackable", parseNullableBool(e.target.value))
                  }
                >
                  <option value="">—</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </Form.Select>
              </MetadataRow>
              <MetadataRow label="Requires Review">
                <Form.Select
                  size="sm"
                  value={
                    metadataForm.requiresReview == null
                      ? ""
                      : String(metadataForm.requiresReview)
                  }
                  onChange={(e) =>
                    updateMeta(
                      "requiresReview",
                      parseNullableBool(e.target.value)
                    )
                  }
                >
                  <option value="">—</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </Form.Select>
              </MetadataRow>
              <MetadataRow label="Reviewed">
                <Form.Select
                  size="sm"
                  value={
                    metadataForm.isReviewed == null
                      ? ""
                      : String(metadataForm.isReviewed)
                  }
                  onChange={(e) =>
                    updateMeta("isReviewed", parseNullableBool(e.target.value))
                  }
                >
                  <option value="">—</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </Form.Select>
              </MetadataRow>
              <MetadataRow label="Reasoning">
                <Form.Control
                  size="sm"
                  as="textarea"
                  rows={2}
                  value={metadataForm.reasoning ?? ""}
                  onChange={(e) =>
                    updateMeta("reasoning", e.target.value || null)
                  }
                />
              </MetadataRow>
              {metadata && (
                <>
                  <MetadataRow label="Created">
                    {formatDate(metadata.createdAt)}
                  </MetadataRow>
                  <MetadataRow label="Updated">
                    {formatDate(metadata.updatedAt)}
                  </MetadataRow>
                </>
              )}
              <MetadataRow label={hashLabel}>
                <code className="small">{hashValue ?? "—"}</code>
              </MetadataRow>
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mb-3">
      <Card.Header>Metadata</Card.Header>
      <Card.Body className="p-0">
        <Table size="sm" borderless className="mb-0">
          <tbody>
            {/* ── Promo classification ── */}
            <MetadataRow label="Promo Type">
              {promoTypeLabel(metadata!.promoType)}
            </MetadataRow>
            <MetadataRow label="Coverage">
              {promoCoverageLabel(metadata!.coverage)}
            </MetadataRow>
            <MetadataRow label="Scope">
              {promoScopeLabel(metadata!.scope)}
            </MetadataRow>
            <MetadataRow label="Channel">
              {promoChannelLabel(metadata!.channel)}
            </MetadataRow>

            {/* ── Discount details ── */}
            <MetadataRow
              label="Discount Type"
              hide={metadata!.discountType == null}
            >
              {discountTypeLabel(metadata!.discountType)}
            </MetadataRow>
            <MetadataRow label="Discounts" hide={!metadata!.discounts}>
              <DiscountsDisplay
                raw={metadata!.discounts}
                currency={metadata!.currency}
              />
            </MetadataRow>
            <MetadataRow
              label="Non-Monetary Value"
              hide={!metadata!.nonMonetaryValue}
            >
              {metadata!.nonMonetaryValue}
            </MetadataRow>

            {/* ── Targeting ── */}
            <MetadataRow label="Language" hide={!metadata!.language}>
              {metadata!.language}
            </MetadataRow>
            <MetadataRow label="Currency" hide={!metadata!.currency}>
              {metadata!.currency}
            </MetadataRow>
            <MetadataRow
              label="Product Category"
              hide={!metadata!.productCategory}
            >
              {metadata!.productCategory}
            </MetadataRow>
            <MetadataRow label="Raw Category" hide={!metadata!.rawCategory}>
              {metadata!.rawCategory}
            </MetadataRow>

            {/* ── Review flags ── */}
            <MetadataRow label="Stackable">
              {boolBadge(metadata!.isStackable)}
            </MetadataRow>
            <MetadataRow label="Requires Review">
              {boolBadge(metadata!.requiresReview)}
            </MetadataRow>
            <MetadataRow label="Reviewed">
              {boolBadge(metadata!.isReviewed)}
            </MetadataRow>
            <MetadataRow label="Reasoning" hide={!metadata!.reasoning}>
              {metadata!.reasoning}
            </MetadataRow>

            {/* ── Dates & hash ── */}
            <MetadataRow label="Created">
              {formatDate(metadata!.createdAt)}
            </MetadataRow>
            <MetadataRow label="Updated">
              {formatDate(metadata!.updatedAt)}
            </MetadataRow>
            <MetadataRow label={hashLabel}>
              <code className="small">{hashValue ?? "—"}</code>
            </MetadataRow>
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

const CopyIdButton: React.FC<{ id: string }> = ({ id }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="link"
      size="sm"
      className="p-0 ms-1 align-baseline"
      title={`Copy ${id}`}
      onClick={() => {
        navigator.clipboard.writeText(id);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? (
        <Check size={12} className="text-success" />
      ) : (
        <Copy size={12} />
      )}
    </Button>
  );
};

export const KindredOfferDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data, loading, error, refetch } = useGet<IKindredOfferDetail>(
    `kindred/offers/${id}`,
    true
  );

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<IKindredOfferUpdate>({});
  const [metadataForm, setMetadataForm] = useState<IKindredMetadataUpdate>({});
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [flagsAcknowledged, setFlagsAcknowledged] = useState(false);
  const [comment, setComment] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const updateOffer = usePut<
    IKindredOfferDetail,
    IKindredEntityUpdateRequest<IKindredOfferUpdate>
  >();

  useEffect(() => {
    if (data && editing) {
      setForm({
        brandId: data.brandId,
        publishedStatus: data.publishedStatus,
        summary: data.summary,
        validFrom: data.validFrom,
        validTo: data.validTo,
        priority: data.priority,
        coverage: data.coverage,
        hasProhibitedContents: data.hasProhibitedContents,
        lastTestedAt: data.lastTestedAt,
        overridesFlag: data.overridesFlag ?? 0,
        trackingLink: data.trackingLink,
        promoLink: data.promoLink,
        domains: data.offerDomainDetails || [],
        countries: data.countryDetails?.map((c) => c.countryCode) || [],
      });
      const m = data.metadata;
      setMetadataForm({
        promoType: m?.promoType ?? null,
        coverage: m?.coverage ?? null,
        discountType: m?.discountType ?? null,
        discounts: m?.discounts ?? null,
        nonMonetaryValue: m?.nonMonetaryValue ?? null,
        language: m?.language ?? null,
        currency: m?.currency ?? null,
        scope: m?.scope ?? null,
        channel: m?.channel ?? null,
        productCategory: m?.productCategory ?? null,
        rawCategory: m?.rawCategory ?? null,
        isStackable: m?.isStackable ?? null,
        requiresReview: m?.requiresReview ?? null,
        reasoning: m?.reasoning ?? null,
        isReviewed: m?.isReviewed ?? null,
      });
    }
  }, [data, editing]);

  const startEdit = () => {
    setSaveError(null);
    setSaveSuccess(false);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setForm({});
    setMetadataForm({});
    setComment("");
    setSaveError(null);
    setFlagsAcknowledged(false);
  };

  const buildChanges = (): Partial<IKindredOfferUpdate> => {
    const changed: Partial<IKindredOfferUpdate> = {};
    for (const key of Object.keys(form) as (keyof IKindredOfferUpdate)[]) {
      if (key === "metadata" || key === "domains" || key === "countries")
        continue;
      if (
        data &&
        String(form[key] ?? "") !== String((data as any)[key] ?? "")
      ) {
        (changed as any)[key] = form[key];
      }
    }

    // Detect array field changes (domains, countries)
    const origDomains = data?.offerDomainDetails || [];
    if (JSON.stringify(form.domains) !== JSON.stringify(origDomains)) {
      changed.domains = form.domains;
    }
    const origCountries = data?.countryDetails?.map((c) => c.countryCode) || [];
    if (JSON.stringify(form.countries) !== JSON.stringify(origCountries)) {
      changed.countries = form.countries;
    }

    // Detect metadata changes
    const metaChanged: IKindredMetadataUpdate = {};
    const origMeta = data?.metadata;
    for (const key of Object.keys(
      metadataForm
    ) as (keyof IKindredMetadataUpdate)[]) {
      const newVal = metadataForm[key];
      const oldVal = origMeta ? (origMeta as any)[key] : undefined;
      if (String(newVal ?? "") !== String(oldVal ?? "")) {
        (metaChanged as any)[key] = newVal;
      }
    }
    if (Object.keys(metaChanged).length > 0) {
      changed.metadata = metaChanged;
    }
    return changed;
  };

  const pendingFlag = Number(form.overridesFlag ?? data?.overridesFlag ?? 0);
  const missingFlags = missingOverrides(
    buildChanges(),
    pendingFlag,
    OFFER_OVERRIDE_REQUIREMENTS
  );

  const handleSave = async () => {
    try {
      setSaveError(null);
      const changed = buildChanges();

      if (Object.keys(changed).length === 0) {
        setSaveError("No changes detected");
        return;
      }
      if (changed.publishedStatus !== undefined) {
        changed.publishedStatus = Number(changed.publishedStatus);
      }
      if (changed.priority !== undefined) {
        changed.priority = Number(changed.priority);
      }
      if (changed.overridesFlag !== undefined) {
        changed.overridesFlag = Number(changed.overridesFlag);
      }
      await updateOffer.execute(`kindred/offers/${id}`, {
        data: changed,
        comment,
      });
      setShowSaveModal(false);
      setEditing(false);
      setComment("");
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 4000);
      await refetch();
    } catch (e: any) {
      setSaveError(e?.message || "Failed to save changes");
    }
  };

  const updateField = (field: keyof IKindredOfferUpdate, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateMetadataField = (
    field: keyof IKindredMetadataUpdate,
    value: any
  ) => {
    setMetadataForm((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" /> Loading offer...
      </div>
    );
  }

  if (error) {
    return <div className="text-danger">Error: {error.message}</div>;
  }

  if (!data) {
    return <div className="text-muted">Offer not found.</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>Offer Detail</h4>
        <div className="d-flex gap-2">
          <Link to="/kindred/offers">
            <Button size="sm" variant="outline-secondary">
              ← Back to Offers
            </Button>
          </Link>
          <Link to={`/kindred/offers/${id}/audit`}>
            <Button size="sm" variant="outline-info">
              View Audit Trail
            </Button>
          </Link>
          {!editing ? (
            <Button size="sm" variant="primary" onClick={startEdit}>
              <Edit2 size={14} className="me-1" /> Edit
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={cancelEdit}
              >
                <X size={14} className="me-1" /> Cancel
              </Button>
              <Button
                size="sm"
                variant="success"
                onClick={() => setShowSaveModal(true)}
              >
                <Save size={14} className="me-1" /> Save
              </Button>
            </>
          )}
        </div>
      </div>

      {saveSuccess && (
        <Alert
          variant="success"
          dismissible
          onClose={() => setSaveSuccess(false)}
        >
          Offer updated successfully.
        </Alert>
      )}
      {saveError && !showSaveModal && (
        <Alert variant="danger" dismissible onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      <Row className="mb-3">
        <Col md={6}>
          <Card>
            <Card.Header>Offer Details</Card.Header>
            <Card.Body>
              <Table size="sm" borderless>
                <tbody>
                  <tr>
                    <td className="fw-bold" style={{ width: "40%" }}>
                      ID
                    </td>
                    <td>{data.id}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Brand</td>
                    <td>
                      {editing ? (
                        <BrandTypeahead
                          brandId={form.brandId || data.brandId}
                          brandName={data.brandName}
                          onChange={(brandId) =>
                            updateField("brandId", brandId)
                          }
                        />
                      ) : (
                        <>
                          <Link to={`/kindred/brands/${data.brandId}`}>
                            {data.brandName}
                          </Link>
                          {data.isTopBrand && (
                            <Badge bg="info" className="ms-1">
                              ★
                            </Badge>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Status</td>
                    <td>
                      {editing ? (
                        <Form.Select
                          size="sm"
                          value={form.publishedStatus ?? 0}
                          onChange={(e) =>
                            updateField(
                              "publishedStatus",
                              Number(e.target.value)
                            )
                          }
                        >
                          <option value={0}>Draft</option>
                          <option value={1}>Awaiting Approval</option>
                          <option value={2}>Approved</option>
                          <option value={3}>Deactivated</option>
                        </Form.Select>
                      ) : (
                        statusLabel(data.publishedStatus)
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Source</td>
                    <td>{data.source}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Priority</td>
                    <td>
                      {editing ? (
                        <Form.Control
                          size="sm"
                          type="number"
                          value={form.priority ?? 0}
                          onChange={(e) =>
                            updateField("priority", Number(e.target.value))
                          }
                        />
                      ) : (
                        data.priority
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Offer Hash</td>
                    <td>
                      <code className="small">{data.offerHash}</code>
                    </td>
                  </tr>
                  {(data.trackingLink || editing) && (
                    <tr>
                      <td className="fw-bold">Tracking Link</td>
                      <td>
                        {editing ? (
                          <Form.Control
                            size="sm"
                            value={form.trackingLink ?? ""}
                            onChange={(e) =>
                              updateField("trackingLink", e.target.value)
                            }
                          />
                        ) : (
                          <a
                            href={data.trackingLink!}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {data.trackingLink!.length > 60
                              ? `${data.trackingLink!.substring(0, 60)}…`
                              : data.trackingLink}
                          </a>
                        )}
                      </td>
                    </tr>
                  )}
                  {(data.promoLink || editing) && (
                    <tr>
                      <td className="fw-bold">Promo Link</td>
                      <td>
                        {editing ? (
                          <Form.Control
                            size="sm"
                            value={form.promoLink ?? ""}
                            onChange={(e) =>
                              updateField("promoLink", e.target.value)
                            }
                          />
                        ) : (
                          <a
                            href={data.promoLink!}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {data.promoLink!.length > 60
                              ? `${data.promoLink!.substring(0, 60)}…`
                              : data.promoLink}
                          </a>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card>
            <Card.Header>Dates & Flags</Card.Header>
            <Card.Body>
              <Table size="sm" borderless>
                <tbody>
                  <tr>
                    <td className="fw-bold" style={{ width: "40%" }}>
                      Valid From
                    </td>
                    <td>
                      {editing ? (
                        <Form.Control
                          size="sm"
                          type="datetime-local"
                          value={
                            form.validFrom
                              ? form.validFrom.substring(0, 16)
                              : ""
                          }
                          onChange={(e) =>
                            updateField(
                              "validFrom",
                              e.target.value
                                ? new Date(e.target.value).toISOString()
                                : ""
                            )
                          }
                        />
                      ) : (
                        formatDate(data.validFrom)
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Valid To</td>
                    <td>
                      {editing ? (
                        <Form.Control
                          size="sm"
                          type="datetime-local"
                          value={
                            form.validTo ? form.validTo.substring(0, 16) : ""
                          }
                          onChange={(e) =>
                            updateField(
                              "validTo",
                              e.target.value
                                ? new Date(e.target.value).toISOString()
                                : ""
                            )
                          }
                        />
                      ) : (
                        formatDate(data.validTo)
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Expiry Date</td>
                    <td>{formatDate(data.expiryDate)}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Deactivated At</td>
                    <td>{formatDate(data.deactivatedAt)}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Created</td>
                    <td>{formatDate(data.createdAt)}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Updated</td>
                    <td>{formatDate(data.updatedAt)}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Last Tested</td>
                    <td>
                      {editing ? (
                        <Form.Control
                          size="sm"
                          type="datetime-local"
                          value={
                            form.lastTestedAt
                              ? form.lastTestedAt.substring(0, 16)
                              : data.lastTestedAt
                                ? data.lastTestedAt.substring(0, 16)
                                : ""
                          }
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              lastTestedAt: e.target.value
                                ? new Date(e.target.value).toISOString()
                                : undefined,
                            }))
                          }
                        />
                      ) : (
                        formatDate(data.lastTestedAt)
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Coverage</td>
                    <td>
                      {editing ? (
                        <Form.Select
                          size="sm"
                          value={form.coverage ?? ""}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              coverage:
                                e.target.value === ""
                                  ? undefined
                                  : Number(e.target.value),
                            }))
                          }
                        >
                          <option value="">—</option>
                          <option value={0}>Not Set</option>
                          <option value={1}>Site-Wide</option>
                          <option value={2}>Category</option>
                          <option value={4}>Product Specific</option>
                        </Form.Select>
                      ) : (
                        (() => {
                          const map: Record<number, string> = {
                            0: "Not Set",
                            1: "Site-Wide",
                            2: "Category",
                            4: "Product Specific",
                          };
                          return map[data.coverage] ?? String(data.coverage);
                        })()
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Prohibited Contents</td>
                    <td>
                      {editing ? (
                        <Form.Select
                          size="sm"
                          value={form.hasProhibitedContents ?? 0}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              hasProhibitedContents: Number(e.target.value),
                            }))
                          }
                        >
                          <option value={0}>No</option>
                          <option value={1}>Yes</option>
                        </Form.Select>
                      ) : data.hasProhibitedContents ? (
                        "Yes"
                      ) : (
                        "No"
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Overrides Flag</td>
                    <td>
                      {editing ? (
                        <OverrideFlagsEditor
                          value={form.overridesFlag ?? 0}
                          onChange={(overridesFlag) =>
                            setForm((prev) => ({ ...prev, overridesFlag }))
                          }
                          flagEnum={OfferOverridesFlag}
                        />
                      ) : (
                        <OverrideFlagsEditor
                          value={data.overridesFlag}
                          flagEnum={OfferOverridesFlag}
                          readOnly
                        />
                      )}
                    </td>
                  </tr>
                  {data.reason && (
                    <tr>
                      <td className="fw-bold">Reason</td>
                      <td>{data.reason}</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {(data.summary || editing) && (
        <Card className="mb-3">
          <Card.Header>Summary</Card.Header>
          <Card.Body>
            {editing ? (
              <Form.Control
                as="textarea"
                rows={3}
                value={form.summary ?? ""}
                onChange={(e) => updateField("summary", e.target.value)}
              />
            ) : (
              <p>{data.summary}</p>
            )}
          </Card.Body>
        </Card>
      )}

      <MetadataCard
        metadata={data.metadata}
        hashLabel="Offer Hash"
        hashValue={data.offerHash}
        editing={editing}
        metadataForm={metadataForm}
        onMetadataChange={updateMetadataField}
      />

      <Row>
        <Col md={4}>
          {editing ? (
            <TagListEditor
              label="Countries"
              items={form.countries || []}
              onChange={(countries) =>
                setForm((prev) => ({ ...prev, countries }))
              }
              placeholder="Add country code..."
            />
          ) : (
            <Card className="mb-3">
              <Card.Header>
                Countries ({data.countryDetails?.length ?? 0})
              </Card.Header>
              <Card.Body>
                {data.countryDetails.length === 0 ? (
                  <span className="text-muted">No countries</span>
                ) : (
                  data.countryDetails.map((c) => (
                    <Badge
                      key={c.countryCode}
                      bg={c.isEnabled ? "success" : "secondary"}
                      className="me-1 mb-1"
                    >
                      {c.countryCode}
                      {!c.isEnabled && " (disabled)"}
                    </Badge>
                  ))
                )}
              </Card.Body>
            </Card>
          )}
        </Col>
        <Col md={4}>
          {editing ? (
            <TagListEditor
              label="Offer Domains"
              normalize="lower"
              items={form.domains || []}
              onChange={(domains) => setForm((prev) => ({ ...prev, domains }))}
              placeholder="Add domain..."
            />
          ) : (
            <Card className="mb-3">
              <Card.Header>
                Offer Domains ({data.offerDomainDetails?.length ?? 0})
              </Card.Header>
              <Card.Body>
                {data.offerDomainDetails.length === 0 ? (
                  <span className="text-muted">No offer domains</span>
                ) : (
                  data.offerDomainDetails.map((d) => (
                    <Badge key={d} bg="light" text="dark" className="me-1 mb-1">
                      {d}
                    </Badge>
                  ))
                )}
              </Card.Body>
            </Card>
          )}
        </Col>
        <Col md={8}>
          <CampaignDomainsCard details={data.campaignDomainDetails} />
        </Col>
      </Row>

      {/* Save Comment Modal */}
      <Modal show={showSaveModal} onHide={() => setShowSaveModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Save Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Comment (required)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Describe the reason for this change..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </Form.Group>
          <OverrideFlagWarning
            missing={missingFlags}
            acknowledged={flagsAcknowledged}
            onAcknowledge={setFlagsAcknowledged}
            onApply={() =>
              updateField(
                "overridesFlag",
                withOverrides(pendingFlag, missingFlags)
              )
            }
          />
          {saveError && (
            <Alert variant="danger" className="mt-3 mb-0">
              {saveError}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSaveModal(false)}>
            Cancel
          </Button>
          <Button
            variant="success"
            disabled={
              !comment.trim() ||
              updateOffer.loading ||
              (missingFlags.length > 0 && !flagsAcknowledged)
            }
            onClick={handleSave}
          >
            {updateOffer.loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

/* ── Campaign Domains card with grouped / detailed toggle ────────── */

const CampaignDomainsCard: React.FC<{
  details: IKindredOfferDetail["campaignDomainDetails"];
}> = ({ details }) => {
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [validatedOnly, setValidatedOnly] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedSource, setSelectedSource] = useState("");

  const brands = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const d of details) {
      if (d.brandId && d.brandName) map.set(d.brandId, d.brandName);
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [details]);

  const sources = React.useMemo(() => {
    const set = new Set<string>();
    for (const d of details) {
      if (d.campaignSource) set.add(d.campaignSource);
    }
    return Array.from(set).sort();
  }, [details]);

  const filtered = React.useMemo(() => {
    let base = validatedOnly ? details.filter((d) => d.validated) : details;
    if (selectedBrand) {
      base = base.filter((d) => d.brandId === selectedBrand);
    }
    if (selectedSource) {
      base = base.filter((d) => d.campaignSource === selectedSource);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      base = base.filter((d) => d.domain.toLowerCase().includes(q));
    }
    return [...base].sort((a, b) => a.domain.localeCompare(b.domain));
  }, [details, validatedOnly, search, selectedBrand, selectedSource]);

  const grouped = React.useMemo(() => {
    const map = new Map<
      string,
      {
        domain: string;
        brandId: string | null;
        brandName: string | null;
        validated: boolean;
        campaignCount: number;
      }
    >();
    for (const d of filtered) {
      const key = `${d.domain}||${d.brandId}||${d.validated}`;
      const existing = map.get(key);
      if (existing) {
        existing.campaignCount++;
      } else {
        map.set(key, {
          domain: d.domain,
          brandId: d.brandId,
          brandName: d.brandName,
          validated: d.validated,
          campaignCount: 1,
        });
      }
    }
    return Array.from(map.values());
  }, [filtered]);

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <span>
          Campaign Domains ({filtered.length}
          {validatedOnly || search.trim() || selectedBrand || selectedSource
            ? ` of ${details.length}`
            : ""}
          )
        </span>
        <div className="d-flex gap-2 align-items-center">
          <select
            className="form-select form-select-sm"
            style={{ width: 180 }}
            value={selectedBrand}
            onChange={(e) => setSelectedBrand(e.target.value)}
          >
            <option value="">All Brands</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <select
            className="form-select form-select-sm"
            style={{ width: 160 }}
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value)}
          >
            <option value="">All Networks</option>
            {sources.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search domain…"
            style={{ width: 200 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button
            variant={validatedOnly ? "success" : "outline-secondary"}
            size="sm"
            onClick={() => setValidatedOnly(!validatedOnly)}
          >
            {validatedOnly ? "Validated Only" : "All"}
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowCampaigns(!showCampaigns)}
          >
            {showCampaigns ? "Group by Domain" : "Show Campaigns"}
          </Button>
        </div>
      </Card.Header>
      <Card.Body style={{ maxHeight: 400, overflowY: "auto", padding: 0 }}>
        {filtered.length === 0 ? (
          <span className="text-muted p-3 d-block">No campaign domains</span>
        ) : showCampaigns ? (
          <Table size="sm" className="mb-0">
            <thead
              style={{
                position: "sticky",
                top: 0,
                background: "#fff",
                zIndex: 1,
              }}
            >
              <tr>
                <th className="ps-3">Domain</th>
                <th>Campaign</th>
                <th>Network</th>
                <th>Brand</th>
                <th>Validated</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d, i) => (
                <tr key={i}>
                  <td className="ps-3">{d.domain}</td>
                  <td>
                    <Link
                      to={`/kindred/deals/${d.campaignId}`}
                      title={d.campaignName ?? d.campaignId}
                    >
                      {d.campaignName
                        ? `${d.campaignName.substring(0, 30)}${d.campaignName.length > 30 ? "…" : ""}`
                        : d.campaignId}
                    </Link>
                  </td>
                  <td>{d.campaignSource ?? "—"}</td>
                  <td>
                    {d.brandName ?? "—"}
                    {d.brandId && <CopyIdButton id={d.brandId} />}
                  </td>
                  <td>
                    {d.validated ? (
                      <Badge bg="success">Yes</Badge>
                    ) : (
                      <Badge bg="secondary">No</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <Table size="sm" className="mb-0">
            <thead
              style={{
                position: "sticky",
                top: 0,
                background: "#fff",
                zIndex: 1,
              }}
            >
              <tr>
                <th className="ps-3">Domain</th>
                <th>Brand</th>
                <th>Validated</th>
                <th>Campaigns</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((g, i) => (
                <tr key={i}>
                  <td className="ps-3">{g.domain}</td>
                  <td>
                    {g.brandName ?? "—"}
                    {g.brandId && <CopyIdButton id={g.brandId} />}
                  </td>
                  <td>
                    {g.validated ? (
                      <Badge bg="success">Yes</Badge>
                    ) : (
                      <Badge bg="secondary">No</Badge>
                    )}
                  </td>
                  <td>
                    <Badge bg="light" text="dark">
                      {g.campaignCount}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
};
